// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Net re-indexing for spliced objects (spec §6.6 step 2, §3.1).
 *
 * This is the single most dangerous operation in the tool. Copying an object
 * between files without remapping its net index through the net *name* silently
 * attaches copper to the wrong net: no error, no warning, valid file, wrong
 * board. Everything here therefore fails loudly rather than guessing.
 */

import { atom, atoms, isNode, type Node } from '../sexp/index.ts';
import type { Board } from '../model/board.ts';

export class NetRemapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetRemapError';
  }
}

export interface NetRemapStats {
  /** How many `(net …)` references were rewritten. */
  rewritten: number;
  /** Net names encountered, for the merge report. */
  names: Set<string>;
  /**
   * Nets the spliced objects bring into existence.
   *
   * Only possible where nets are named inline (KiCad 10): the object carries
   * its own net name, so a name the destination has never seen is a new net
   * rather than a dangling reference. Worth reporting, never fatal.
   */
  introduced: Set<string>;
}

/**
 * Rewrite every `(net …)` reference inside `subtree` from `source`'s indices to
 * `target`'s, matching on the net *name*.
 *
 * Handles all three spellings:
 *   - `(net 12)`            — segments, arcs, vias, zones
 *   - `(net 12 "SIGNAL")`   — footprint pads
 *   - `(net_name "SIGNAL")` — zones (name only; validated, not rewritten)
 *
 * Throws if a net index is not in the source table, or a net name is not in the
 * target table. V1 should already have caught the latter, so reaching it means
 * either verification was skipped or there is a bug — either way, stopping is
 * the only safe answer.
 */
export function remapNets(subtree: Node, source: Board, target: Board, stats?: NetRemapStats): void {
  const record = stats ?? { rewritten: 0, names: new Set<string>(), introduced: new Set<string>() };
  walk(subtree, source, target, record);
}

function walk(n: Node, source: Board, target: Board, stats: NetRemapStats): void {
  for (const child of n.children) {
    if (!isNode(child)) continue;

    if (child.name === 'net') {
      remapOne(child, source, target, stats);
      continue;
    }
    if (child.name === 'net_name') {
      const name = atoms(child)[0]?.value;
      if (name !== undefined && name !== '' && !target.allNetNames().has(name)) {
        throw new NetRemapError(
          `net name "${name}" is referenced by a spliced object but does not exist in ${target.fileName}. ` +
            `Splicing it would leave a dangling net reference.`,
        );
      }
      continue;
    }
    walk(child, source, target, stats);
  }
}

function remapOne(netNode: Node, source: Board, target: Board, stats: NetRemapStats): void {
  const values = atoms(netNode);
  const first = values[0];
  if (first === undefined) return;

  // KiCad 10 names the net inline. There is no index to translate — which
  // removes the §3.1 hazard entirely for those files — but the name still has
  // to exist on the other side, or the spliced object would reference a net
  // nothing else on the board knows about.
  if (first.quoted) {
    // An inline name is self-describing: there is no table to index into, so
    // the object arriving *is* the definition of that net. A name the
    // destination has not seen therefore creates a net rather than dangling —
    // refusing it would make it impossible to merge any new signal into a
    // KiCad 10 board, which is most of what merging is for.
    const name = first.value;
    if (name !== '' && !target.allNetNames().has(name)) stats.introduced.add(name);
    stats.names.add(name);
    return;
  }

  const sourceIndex = Number(first.value);
  if (!Number.isFinite(sourceIndex)) return;

  const name = source.netNameByIndex.get(sourceIndex);
  if (name === undefined) {
    throw new NetRemapError(
      `a spliced object references net index ${sourceIndex}, which is not in ${source.fileName}'s net table. ` +
        `The source file is inconsistent; refusing to guess which net was meant.`,
    );
  }

  const targetIndex = target.netIndexByName.get(name);
  if (targetIndex === undefined) {
    throw new NetRemapError(
      `net "${name}" exists in ${source.fileName} but not in ${target.fileName}. ` +
        `Check V1 — a merge cannot invent a net index for it.`,
    );
  }

  values[0]!.value = String(targetIndex);
  values[0]!.quoted = false;
  // Keep the pad's `(net N "NAME")` spelling consistent with the new index.
  if (values.length > 1) {
    values[1]!.value = name;
    values[1]!.quoted = true;
  }
  stats.rewritten++;
  stats.names.add(name);
}

/** Build the report line describing what was remapped. */
export function describeNetRemap(stats: NetRemapStats): string {
  const introduced =
    stats.introduced.size === 0
      ? ''
      : ` ${stats.introduced.size} net(s) new to Board A arrived with the objects that name them: ` +
        `${[...stats.introduced].slice(0, 6).join(', ')}${stats.introduced.size > 6 ? '…' : ''}.`;
  if (stats.rewritten === 0) return `No net references needed remapping.${introduced}`;
  return `${stats.rewritten} net reference(s) re-indexed by name across ${stats.names.size} net(s).${introduced}`;
}

/** Fresh UUID node value. Split out so tests can inject a deterministic source. */
export function defaultNewUuid(): string {
  return globalThis.crypto.randomUUID();
}

export { atom as _atom };
